"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.membersToQueryString = exports.childrenToQueryString = exports.spouseToQueryString = exports.applicantToQueryString = void 0;
const index_1 = require("../../index");
/**
 * Convert a member to a query string.
 *
 * @private
 * @param {string} suffix - The suffix to use for the query string.
 * @param {IMember} member - The member to convert to a query string.
 *
 * @returns {string} - The query string representation of the member.
 *
 * @example
 * ```ts
 * const result = memberToQueryString('', {
 *   id: '0',
 *   type: MemberType.Applicant,
 *   gender: Gender.Male,
 *   dob: new Date('01/01/1990)
 *   age: 21,
 *   stringDOB: '01/01/1990
 * });
 * console.log(result);
 * // -> 'dob=01/01/1990&gender=f'
 * ```
 */
function memberToQueryString(suffix, member) {
    if (!member) {
        return '';
    }
    const { stringDOB, gender, tobacco, firstName, weight, height, lastName } = member;
    return [
        `dob${suffix}=${stringDOB}`,
        `gender${suffix}=${gender}`,
        tobacco && `tobacco${suffix}=${tobacco}`,
        firstName && `fname${suffix}=${firstName}`,
        lastName && `lname${suffix}=${lastName}`,
        weight && `weight${suffix}=${weight}`,
        height && `height${suffix}=${height.feet}_${height.inches}`,
    ]
        .filter(Boolean)
        .join('&');
}
/**
 * Convert an Applicant to a query string.
 *
 * @public
 * @param {IApplicant} members - The Applicant to convert to a query string.
 *
 * @returns {string} - The query string representation of the members.
 *
 * @example
 * ```ts
 * const result = applicantToQueryString({
 *   id: '0',
 *   type: MemberType.Applicant,
 *   gender: Gender.Male,
 *   dob: new Date('01/01/1990)
 *   age: 21,
 *   stringDOB: '01/01/1990
 * });
 * console.log(result);
 * // -> 'dob=01/01/1990&gender=f'
 * ```
 */
function applicantToQueryString(applicant) {
    return memberToQueryString('', applicant);
}
exports.applicantToQueryString = applicantToQueryString;
/**
 * Convert an Spouse to a query string.
 *
 * @public
 * @param {ISpouse} members - The Spouse to convert to a query string.
 *
 * @returns {string} - The query string representation of the members.
 *
 * @example
 * ```ts
 * const result = spouseToQueryString({
 *   id: '0',
 *   type: MemberType.Spouse,
 *   gender: Gender.Male,
 *   dob: new Date('01/01/1990)
 *   age: 21,
 *   stringDOB: '01/01/1990
 * });
 * console.log(result);
 * // -> 'dob_s=01/01/1990&gender_s=f'
 * ```
 */
function spouseToQueryString(spouse) {
    return memberToQueryString('_s', spouse);
}
exports.spouseToQueryString = spouseToQueryString;
/**
 * Convert children to a query string.
 *
 * @public
 * @param {IChild[]} members - The children to convert to a query string.
 *
 * @returns {string} - The query string representation of the members.
 *
 * @example
 * ```ts
 * const result = childrenToQueryString([{
 *   id: '0',
 *   type: MemberType.Child,
 *   gender: Gender.Male,
 *   dob: new Date('01/01/1990)
 *   age: 21,
 *   stringDOB: '01/01/1990
 * }]);
 * console.log(result);
 * // -> 'dob_1=01/01/1990&gender_1=f'
 * ```
 */
function childrenToQueryString(children) {
    return children
        .map((child, i) => memberToQueryString(`_${i + 1}`, child))
        .filter(Boolean)
        .join('&');
}
exports.childrenToQueryString = childrenToQueryString;
/**
 * Convert a list of members to a query string.
 *
 * @public
 * @param {IMember[]} members - The members to convert to a query string.
 *
 * @returns {string} - The query string representation of the members.
 *
 * @example
 * ```ts
 * const result = membersToQueryString([{
 *   id: '0',
 *   type: MemberType.Applicant,
 *   gender: Gender.Male,
 *   dob: new Date('01/01/1990)
 *   age: 21,
 *   stringDOB: '01/01/1990
 * }, {
 *   id: '0',
 *   type: MemberType.Spouse,
 *   gender: Gender.Male,
 *   dob: new Date('01/01/1990)
 *   age: 21,
 *   stringDOB: '01/01/1990
 * }]);
 * console.log(result);
 * // -> 'dob=01/01/1990&gender=f&dob_s=01/01/1990&gender_s=f'
 * ```
 */
function membersToQueryString(members) {
    return [
        applicantToQueryString((0, index_1.findApplicant)(members)),
        spouseToQueryString((0, index_1.findSpouse)(members)),
        childrenToQueryString((0, index_1.findChildren)(members)),
    ]
        .filter(Boolean)
        .join('&');
}
exports.membersToQueryString = membersToQueryString;
//# sourceMappingURL=members-to-query-string.js.map